import { useState } from 'react';
import { GitBranch, MessageSquare, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useChangeRequests } from '../../context/ChangeRequestProvider';
import {
  Caret,
  WarnIcon,
  d,
  fmtDate,
  humanise,
  needsAttention,
  prereqCounts,
  readinessTone,
  riskState,
  riskTone,
} from './InsightsGantt';
import { useAccess } from '../../services/access';
import ShareButton from '../access/ShareButton';
import { Chip, T } from './shared';

/** Plain expandable text row, not a card — no border/background box of its
 * own; a hairline border-b (see the `border-b` divide pattern on the group
 * wrapper below) separates one from the next instead. Was a bordered,
 * backgrounded, hover-filled box, which read as louder than the plain
 * content around it for what's essentially a Jira-mirror reference list. */
const Section = ({ title, count, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-2.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 text-left"
      >
        <span className="flex items-center gap-2">
          <Caret open={open} />
          <span className="text-xs font-semibold text-zinc-300">{title}</span>
        </span>
        {count != null && (
          <span className="text-[11px] font-medium text-zinc-500">{count}</span>
        )}
      </button>
      {open && <div className="mt-2 pl-5">{children}</div>}
    </div>
  );
};

/** One row-list inside Impact & Dependencies (upstream or downstream).
 * A single item shows a plain label — nothing to collapse behind. More
 * than one gets a toggle, collapsed by default, same as the Readiness
 * Evaluation prerequisites list. */
const DependencyGroup = ({ label, items, open, onToggle, renderItem }) => {
  if (items.length === 0) return null;
  return (
    <div className="mb-3 last:mb-0">
      {items.length > 1 ? (
        <button
          type="button"
          onClick={onToggle}
          className="mb-1.5 flex w-full items-center gap-1.5 text-left"
        >
          <Caret open={open} />
          <span className="text-[11px] font-medium text-zinc-400">
            {label} ({items.length})
          </span>
        </button>
      ) : (
        <div className="mb-1.5 text-[11px] font-medium text-zinc-400">
          {label}
        </div>
      )}
      {(items.length === 1 || open) && (
        <div className="space-y-1.5">{items.map(renderItem)}</div>
      )}
    </div>
  );
};

/**
 * Detail content for one deliverable: answers "can this happen as planned"
 * first (readiness/risk/why), then Impact, catalogue learnings, and finally
 * the Jira-mirror sections (scope/SOP/acceptance criteria/prerequisites)
 * collapsed by default. Rendered inside the register's `DrawerDetail`
 * slide-in matching the signature Oqlous dark theme.
 */
const GanttDetailPanel = ({
  ticket,
  allTickets = [],
  onClose,
  onOpenTicket,
}) => {
  const navigate = useNavigate();
  const { stageCRChat } = useChangeRequests();
  const canChat = useAccess().can('chat:send');
  // Collapsed by default — only matters when there's more than one
  // unresolved prerequisite / upstream blocker / downstream deliverable
  // to begin with (see the respective toggles below).
  const [prereqsOpen, setPrereqsOpen] = useState(false);
  const [upstreamOpen, setUpstreamOpen] = useState(false);
  const [downstreamOpen, setDownstreamOpen] = useState(false);

  if (!ticket) return null;

  const rs = riskState(ticket);
  const risk = riskTone(rs);
  const rd = readinessTone(ticket.readiness_status);
  const pc = prereqCounts(ticket);
  const downstream = allTickets.filter((t) =>
    ticket.blocks_deliverables?.includes(t.deliverable_id),
  );
  const upstream = allTickets.filter((t) =>
    ticket.blocked_by_deliverables?.includes(t.deliverable_id),
  );
  const openApprovals = (ticket.approvals ?? []).filter(
    (a) => a.status === 'PENDING',
  );

  const handleChatWithAgent = () => {
    stageCRChat({
      id: ticket.deliverable_id,
      source_type: 'deliverable',
      event_name: `${ticket.external_ticket_id || ticket.deliverable_id}: ${ticket.jira.title}`,
      timestamp: new Date().toISOString(),
      deliverable_data: ticket,
    });
    onClose();
    navigate('/chat');
  };

  return (
    <div
      className="flex h-full flex-col bg-[#18181b] text-zinc-100"
      aria-label={`Detail for ${ticket.external_ticket_id || ticket.deliverable_id}`}
    >
      <div className="shrink-0 border-b border-zinc-700/80 bg-[#18181b] px-5 pb-3 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-semibold text-zinc-400">
                {ticket.external_ticket_id || 'Internal'}
              </span>
              <Chip fg={risk.fg} bg={risk.bg}>
                {risk.label}
              </Chip>
              <Chip fg={T.muted} bordered>
                {ticket.activity_name}
              </Chip>
              <ShareButton
                itemType="deliverable"
                itemId={ticket.deliverable_id}
                title={ticket.external_ticket_id || ticket.deliverable_id}
              />
              {ticket.client_type_label && (
                <Chip fg={T.info} bg={T.infoBg}>
                  {humanise(ticket.client_type_label)}
                </Chip>
              )}
            </div>
            <h2 className="mt-2.5 text-[17px] font-bold leading-snug text-zinc-100">
              {ticket.jira.title}
            </h2>
            {ticket.client_requirement && (
              <p className="mt-1.5 text-[12px] leading-relaxed text-zinc-400">
                {ticket.client_requirement}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canChat && (
              <button
                type="button"
                onClick={handleChatWithAgent}
                className="flex items-center gap-1.5 rounded-xl bg-primary-gradient px-3 py-1.5 text-xs font-semibold text-white shadow-md shadow-indigo-950/40 transition-all hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40"
              >
                <MessageSquare size={13} className="text-white" />
                <span>Bring to chat</span>
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
              aria-label="Close panel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        {/* One shared border/bg for the whole stat block instead of six
            individually-bordered cards — same label/value pairs, one
            container instead of six borders + six backgrounds + six
            shadows stacked next to each other. */}
        <div className="shadow-xs mb-3 grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 sm:grid-cols-3">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              Status
            </div>
            <div className="mt-0.5 text-[13px] font-semibold text-zinc-100">
              {ticket.jira.status}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              Readiness
            </div>
            <div
              className="mt-0.5 text-[13px] font-semibold"
              style={{ color: rd.fg }}
            >
              {rd.label}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              Owner
            </div>
            <div className="mt-0.5 text-[13px] font-semibold text-zinc-100">
              {ticket.jira.owner}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              Due
            </div>
            <div
              className="mt-0.5 text-[13px] font-semibold"
              style={{ color: rs === 'OVERDUE' ? T.danger : T.text }}
            >
              {fmtDate(ticket.jira.due_date)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              Started
            </div>
            <div
              className="mt-0.5 text-[13px] font-semibold"
              style={{ color: ticket.jira.started_at ? T.text : T.muted }}
            >
              {ticket.jira.started_at
                ? fmtDate(ticket.jira.started_at)
                : 'Not started'}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-medium uppercase tracking-wide text-zinc-400">
              Prerequisites
            </div>
            <div
              className="mt-0.5 text-[13px] font-semibold"
              style={{ color: pc.open ? T.warn : T.ok }}
            >
              {pc.pass}/{pc.total} passing
            </div>
          </div>
        </div>

        {(ticket.readiness_status !== 'READY' || needsAttention(ticket)) && (
          <div className="mb-3 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-2.5 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <WarnIcon color={risk.fg} size={14} />
              <span
                className="text-xs font-bold uppercase tracking-wider"
                style={{ color: risk.fg }}
              >
                Readiness Evaluation
              </span>
            </div>
            <p className="text-[13px] font-medium leading-relaxed text-zinc-200">
              {ticket.readiness_basis ||
                'No readiness note recorded for this deliverable.'}
            </p>

            {pc.unresolved.length > 0 && (
              <div className="mt-3 border-t border-amber-500/20 pt-2.5">
                {/* A single item doesn't need a toggle to hide behind —
                    only collapsible once there's actually a list to save
                    space on. */}
                {pc.unresolved.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setPrereqsOpen((v) => !v)}
                    className="flex w-full items-center gap-1.5 text-left"
                  >
                    <Caret open={prereqsOpen} />
                    <span className="text-[11px] font-medium text-zinc-400">
                      {pc.unresolved.length} unresolved prerequisites
                    </span>
                  </button>
                )}
                {(pc.unresolved.length === 1 || prereqsOpen) && (
                  <ul
                    className={`space-y-1.5 ${pc.unresolved.length > 1 ? 'mt-1.5' : ''}`}
                  >
                    {pc.unresolved.map((p) => (
                      <li
                        key={p.id}
                        className="flex gap-2 text-[12px] text-zinc-300"
                      >
                        <span
                          className="font-bold"
                          style={{
                            color:
                              p.verification_status === 'FAIL'
                                ? T.danger
                                : T.warn,
                          }}
                        >
                          {p.verification_status === 'FAIL' ? '✕' : '○'}
                        </span>
                        <span>{p.text || p.label || p.title}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {ticket.open_assumptions?.length > 0 && (
              <div className="mt-2.5 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                  Assumptions
                </div>
                <ul className="mt-2 space-y-1.5">
                  {ticket.open_assumptions.map((a, i) => (
                    <li key={i} className="text-[12px] text-zinc-300">
                      {a.text}{' '}
                      <span
                        className="font-medium"
                        style={{
                          color:
                            d(a.verify_by) < new Date() ? T.danger : T.warn,
                        }}
                      >
                        · verify by {fmtDate(a.verify_by)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ticket.open_concerns?.length > 0 && (
              <div className="mt-2.5 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                  Open Concerns
                </div>
                <ul className="mt-2 space-y-1.5">
                  {ticket.open_concerns.map((c) => (
                    <li
                      key={c.concern_id}
                      className="text-[12px] text-zinc-300"
                    >
                      <span className="font-semibold text-zinc-400">
                        {c.concern_id}
                      </span>{' '}
                      · {c.description}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {openApprovals.length > 0 && (
              <div className="mt-2.5 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
                  Pending Approvals
                </div>
                <ul className="mt-2 space-y-1.5">
                  {openApprovals.map((a, i) => (
                    <li key={i} className="text-[12px] text-zinc-300">
                      <span className="font-semibold text-indigo-300">
                        {a.gate}
                      </span>{' '}
                      <span className="text-zinc-400">· {a.approver}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {(downstream.length > 0 || upstream.length > 0) && (
          <div className="mb-3 rounded-2xl border border-sky-500/30 bg-sky-500/10 p-2.5 shadow-sm">
            <div className="mb-2 flex items-center gap-2">
              <GitBranch className="h-3.5 w-3.5 text-sky-400" />
              <span className="text-xs font-bold uppercase tracking-wider text-sky-400">
                Impact & Dependencies
              </span>
            </div>
            <DependencyGroup
              label="Waiting on (Upstream Blockers)"
              items={upstream}
              open={upstreamOpen}
              onToggle={() => setUpstreamOpen((v) => !v)}
              renderItem={(u) => (
                <button
                  key={u.deliverable_id}
                  type="button"
                  onClick={() => onOpenTicket?.(u.deliverable_id)}
                  className="shadow-xs flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 text-left transition-all hover:border-zinc-600 hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40"
                >
                  <span className="truncate text-[13px] font-medium text-zinc-200">
                    {u.jira.title}
                  </span>
                  <Chip
                    fg={riskTone(riskState(u)).fg}
                    bg={riskTone(riskState(u)).bg}
                  >
                    {riskTone(riskState(u)).label}
                  </Chip>
                </button>
              )}
            />
            <DependencyGroup
              label="If this slips (Downstream Deliverables)"
              items={downstream}
              open={downstreamOpen}
              onToggle={() => setDownstreamOpen((v) => !v)}
              renderItem={(dn) => (
                <button
                  key={dn.deliverable_id}
                  type="button"
                  onClick={() => onOpenTicket?.(dn.deliverable_id)}
                  className="shadow-xs flex w-full items-center justify-between gap-3 rounded-xl border border-zinc-700/60 bg-zinc-800/50 p-2.5 text-left transition-all hover:border-zinc-600 hover:bg-zinc-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#5652CF]/40"
                >
                  <span className="truncate text-[13px] font-medium text-zinc-200">
                    {dn.jira.title}
                  </span>
                  <span className="shrink-0 text-[11px] font-medium text-zinc-400">
                    due {fmtDate(dn.jira.due_date)}
                  </span>
                </button>
              )}
            />
          </div>
        )}

        {ticket.learnings?.length > 0 && (
          <div className="mb-3 rounded-2xl border border-purple-500/30 bg-purple-500/10 p-2.5 shadow-sm">
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-purple-400">
              Catalogue Learnings
            </div>
            <div className="space-y-2">
              {ticket.learnings.map((l) => (
                <div
                  key={l.learning_id}
                  className="rounded-xl border border-purple-500/20 bg-zinc-800/60 p-2.5"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-semibold text-zinc-400">
                      {l.learning_id}
                    </span>
                    <Chip fg={T.decision} bordered>
                      {humanise(l.knowledge_area)}
                    </Chip>
                    <Chip fg={T.muted} bordered>
                      {humanise(l.change_type)}
                    </Chip>
                    <span className="text-[10px] font-medium text-zinc-400">
                      seen ×{l.occurrence_count}
                    </span>
                  </div>
                  <p className="mt-2 text-[13px] font-medium text-zinc-200">
                    {l.proposed_value}
                  </p>
                  <p className="mt-1 text-[10px] text-zinc-400">
                    {humanise(l.lifecycle_state)} · from{' '}
                    {humanise(l.detected_from)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* The Jira-mirror reference lists — a separate, less urgent kind
            of content than everything above (which is all "what's the
            actual status/risk"), so a divider marks the shift instead of
            just another mb-3 gap; divide-y gives hairline separation
            between the four rows instead of each being its own box. */}
        <div className="divide-y divide-zinc-800/60 border-t border-zinc-800 pt-1">
          <Section title="Scope Definition">
            <div className="mb-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
                In scope
              </div>
              <ul className="mt-1.5 space-y-1">
                {(ticket.scope?.in_scope ?? []).map((s, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[13px] text-zinc-200"
                  >
                    <span className="text-emerald-400">✓</span>
                    <span>{s}</span>
                  </li>
                ))}
                {(ticket.scope?.in_scope ?? []).length === 0 && (
                  <li className="text-[12px] text-zinc-500">
                    Nothing recorded yet.
                  </li>
                )}
              </ul>
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-zinc-400">
                Out of scope
              </div>
              <ul className="mt-1.5 space-y-1">
                {(ticket.scope?.out_of_scope ?? []).map((s, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-[13px] text-zinc-400"
                  >
                    <span className="text-zinc-500">✕</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
            {ticket.scope?.corrected && (
              <div className="mt-2.5 rounded-xl border border-purple-500/30 bg-purple-950/20 p-2.5">
                <div className="text-[11px] font-semibold text-purple-300">
                  Corrected by {ticket.scope.corrected_by}
                </div>
                <p className="mt-1 text-[11px] text-zinc-400">
                  was: {ticket.scope.original_value}
                </p>
                <p className="mt-1 text-[12px] text-zinc-200">
                  {ticket.scope.correction_rationale}
                </p>
              </div>
            )}
          </Section>

          <Section
            title="Standard Operating Procedures (SOP)"
            count={ticket.sop_steps.length}
          >
            <ol className="space-y-2">
              {ticket.sop_steps.map((s, i) => (
                <li
                  key={s.id || i}
                  className="flex gap-2.5 text-[13px] text-zinc-200"
                >
                  <span className="font-bold text-zinc-500">{i + 1}.</span>
                  <span>
                    {s.text || s.label}
                    {s.origin && s.origin !== 'CATALOGUE' && (
                      <span className="ml-2 rounded border border-purple-500/30 bg-purple-950/60 px-1.5 py-0.5 text-[10px] font-semibold text-purple-300">
                        {humanise(s.origin)}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ol>
          </Section>

          <Section
            title="Acceptance Criteria"
            count={ticket.acceptance_criteria.length}
          >
            <ul className="space-y-2">
              {ticket.acceptance_criteria.map((c, i) => (
                <li
                  key={c.id || i}
                  className="flex items-start gap-2.5 text-[13px] text-zinc-200"
                >
                  <span
                    className={`font-bold ${c.status === 'MET' ? 'text-emerald-400' : 'text-zinc-500'}`}
                  >
                    {c.status === 'MET' ? '✓' : '○'}
                  </span>
                  <span>{c.text || c.label}</span>
                </li>
              ))}
            </ul>
          </Section>

          <Section title="Prerequisites" count={`${pc.pass}/${pc.total}`}>
            <ul className="space-y-2">
              {ticket.prerequisites.map((p, i) => (
                <li
                  key={p.id || i}
                  className="flex items-start gap-2.5 text-[13px] text-zinc-200"
                >
                  <span
                    className="font-bold"
                    style={{
                      color:
                        p.verification_status === 'PASS' || p.status === 'MET'
                          ? T.ok
                          : p.verification_status === 'FAIL'
                            ? T.danger
                            : T.warn,
                    }}
                  >
                    {p.verification_status === 'PASS' || p.status === 'MET'
                      ? '✓'
                      : p.verification_status === 'FAIL'
                        ? '✕'
                        : '○'}
                  </span>
                  <span>
                    {p.text || p.label || p.title}
                    {p.origin && p.origin !== 'CATALOGUE' && (
                      <span className="ml-2 rounded border border-purple-500/30 bg-purple-950/60 px-1.5 py-0.5 text-[10px] font-semibold text-purple-300">
                        {humanise(p.origin)}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </div>
    </div>
  );
};

export default GanttDetailPanel;
